import React, { useEffect, useMemo, useState } from "react"
import FiltersUI from "@/components/ProductList/v3/ui/FiltersUI"
import ProductCardUI from "@/components/ProductList/v3/ui/ProductCardUI"
import SortUI from "@/components/ProductList/v3/ui/SortUI"
import styles from "@/components/ProductList/v3/styles/ProductList.module.scss"
import { Pagination, PromotionalTilePLP } from "@kohler-dsm-library/kohler-core"
import DOMPurify from "isomorphic-dompurify"
import PartsViewerV2 from "@/components/PartsViewer/v2/PartsViewer"
import { useTranslation } from "next-i18next"
import { productListUIProps } from "@/propTypes/plp"
import { sanitizeText } from "@/utils/textUtils"
import { getShortenedUrl } from "@/utils/location"
import { safeParseJSON, scene7ImageUrl } from "@/utils/helper"
import {
  getPromoClass,
  getPromoType,
  placePromos,
  processPromos,
} from "@/utils/product"
import {
  getPromoEventInfo,
  handlePromoTileDatalayerClick,
} from "@/components/ProductList/v3/analytics/promoTileAnalytics"
import useWindowResize from "@/hooks/useWindowResize"
import {
  mapOverlayDirection,
  mapOverlayOpacity,
} from "@/components/DesignSystem/utils/teaserHelper"

const ProductListUI = ({
  propsPagination,
  propsSortUI,
  propsScroll,
  propsFilters,
  propsProductCard,
  filterClass,
  tileClass,
  componentProps,
  getIsPresentOnDeviceStatus = true,
  position,
  promoCols,
  partData,
  onCloseModal,
  svgFileMap,
  handleOnClickPartsDiagramLink,
  isMobileViewport,
  repeatPromo,
  scene7BaseUrl,
  dmScene7AccountName,
}) => {
  const productCardSKUs = []
  // Parse repeatPromo to determine if promo should repeat
  const shouldRepeatPromo = (() => {
    try {
      return JSON.parse(repeatPromo)
    } catch {
      return false
    }
  })()
  const [actionLink, setActionLink] = useState("")
  const [width] = useWindowResize()
  const [promoLinks, setPromoLinks] = useState({})

  useEffect(() => {
    const parsed = safeParseJSON(componentProps?.xfjson)
    getShortenedUrl(parsed?.ctaLink).then(res => setActionLink(res))
  }, [componentProps])
  const parsedXFJSON = safeParseJSON(componentProps?.xfjson)

  const gradientDictionary = {
    topToBottom: "top-bottom",
    bottomToTop: "bottom-top",
    leftToRight: "left-right",
    rightToLeft: "right-left",
    radialCenterOut: "tint",
  }
  const ctatitle = parsedXFJSON ? sanitizeText(parsedXFJSON?.ctaLabel) : ""
  const titleforPrmobanner = parsedXFJSON
    ? sanitizeText(parsedXFJSON?.title)
    : ""

  const promoEventInfo = getPromoEventInfo(ctatitle.toLowerCase(), actionLink)

  const buildAriaLabel = (ctaTitle = "", context = "") => {
    const cta = ctaTitle.trim()
    let ctx = context
    const newctx = ctx.toLowerCase()
    if (newctx.startsWith("explore")) {
      ctx = newctx.replace("explore", "").trim()
    }

    const normalized = cta.toLowerCase()

    if (normalized.includes("shop")) return `${cta} for ${ctx}`
    if (normalized.includes("learn more")) return `${cta} about ${ctx}`
    if (normalized.includes("read more")) return `${cta} about ${ctx}`
    if (normalized.includes("view")) return `View ${ctx}`
    if (normalized.includes("explore")) return `Explore ${ctx}`

    // safe fallback
    return `${cta} for ${ctx}`
  }

  // Helper function to normalize text placement values
  const normalizeTextPlacement = contentPosition => {
    if (!contentPosition) return ""
    const sanitized = sanitizeText(contentPosition)
    if (!sanitized) return ""
    return sanitized === "dead-center" ? "center" : sanitized
  }

  // Function to create promo props with calculated sequential index
  const createPromoProps = productIndex => {
    // Calculate promo index based on product position
    // Promos appear every `position` products, so we can calculate the sequence number
    const promoSequenceNumber = Math.floor((productIndex + 1) / position)

    const promoComponentInfo = {
      componentID: typeof window !== "undefined" && window.location.href,
      componentLocation:
        typeof window !== "undefined" &&
        window.location.pathname.includes("search")
          ? "search results:promo tiles"
          : "plp:promo tiles",
      componentMessage: `promo-${promoSequenceNumber}`,
      componentName: sanitizeText(parsedXFJSON?.title)
        ?.replace(/<\/?[^>]+(>|$)/g, " ")
        .replace(/(\r\n|\n|\r)/gm, " ")
        .replace(/&nbsp;/g, " ")
        .toLowerCase(),
      componentType: "internal campaign",
    }
    return {
      heading: {
        text: parsedXFJSON ? sanitizeText(parsedXFJSON?.title) : "",
      },
      image: {
        src: parsedXFJSON ? sanitizeText(parsedXFJSON?.imageUrl) : "",
        "data-mode": "smartcrop",
        alt: parsedXFJSON ? sanitizeText(parsedXFJSON?.altText) : "",
        role: "presentation",
      },
      CTA: {
        text: ctatitle,
        href: actionLink,
        target: parsedXFJSON?.isNewTab === "true" ? "_blank" : "_self",
        "aria-label": buildAriaLabel(ctatitle, titleforPrmobanner),
        trackingAttributes: {
          "data-datalayer-id": "kds-element-promotile",
          "data-datalayer-data": JSON.stringify(promoEventInfo) || "",
          "data-datalayer-component": JSON.stringify(promoComponentInfo) || "",
        },
      },
      textPlacement: normalizeTextPlacement(parsedXFJSON?.contentPosition),
      gradientDirection: (() => {
        if (!parsedXFJSON) return "top-bottom"
        const allowedDirections = [
          "top-bottom",
          "bottom-top",
          "left-right",
          "right-left",
          "tint",
        ]
        const getGradientPosition = gp => {
          if (Array.isArray(gp)) return gp[0]
          if (typeof gp === "string") return gp
          return undefined
        }
        const rawValue = getGradientPosition(parsedXFJSON?.gradientPosition)
        const mapped = gradientDictionary[rawValue]
        const normalized = mapOverlayDirection(mapped)
        return normalized && allowedDirections.includes(normalized)
          ? normalized
          : "top-bottom"
      })(),
      opacity:
        parsedXFJSON &&
        mapOverlayOpacity(parsedXFJSON?.gradientOpacity) !== undefined
          ? mapOverlayOpacity(parsedXFJSON?.gradientOpacity)
          : 45,
      type: getPromoType(promoCols[componentProps?.promoLayout]),
      mod: "black",
      style: (() => {
        if (promoCols[componentProps?.promoLayout].includes("2")) {
          return { aspectRatio: "2 / 1" }
        } else if (promoCols[componentProps?.promoLayout].includes("3")) {
          return { aspectRatio: "3 / 1" }
        } else {
          return { aspectRatio: "1 / 1" }
        }
      })(),
    }
  }

  const tiles = useMemo(() => {
    // Filter promos: hide if hidePromo is anything other than "false"
    const filteredPromos = (componentProps?.promos || []).filter(
      promo => promo.hidePromo === "false"
    )
    const promoMap = processPromos(filteredPromos)

    // 2. Merge products and the promo map to get the final tile list
    // CRITICAL FIX: Pass 'width' to placePromos to enable responsive layout logic
    return placePromos(propsProductCard, promoMap, width)
  }, [propsProductCard, componentProps?.promos, width])

  useEffect(() => {
    // Debug logs for promoLinks effect
    // 1. Resolve promo links for each tile after tiles are set
    const promoTiles = tiles.filter(tile => tile?.ctaUrl)

    // 2. Iterate over promotional tiles
    promoTiles.forEach(tile => {
      // 3. Check if the shortened URL is already in the state
      if (!promoLinks[tile.ctaUrl]) {
        // 4. Call getCtalink to initiate the asynchronous shortening process
        getCtalink(tile.ctaUrl)
      }
    })
  }, [tiles, promoLinks])

  useEffect(() => {
    document.addEventListener("click", handlePromoTileDatalayerClick)
    return () =>
      document.removeEventListener("click", handlePromoTileDatalayerClick)
  }, [])
  const isDevicePresent = getIsPresentOnDeviceStatus
  const { t } = useTranslation()
  const getCtalink = url => {
    if (!url) return ""
    if (promoLinks[url]) return promoLinks[url]
    getShortenedUrl(url).then(res => {
      setPromoLinks(prev => ({
        ...prev,
        [url]: res,
      }))
    })
    return url
  }
  return (
    <div className={`${styles.productListWrapper}`}>
      <div className="product-listing white-mod">
        <SortUI propsSortUI={propsSortUI} />
        <div className="container kf-react-plp-container product-listing__tiles">
          <div className="product-listing__row d-block">
            <div className="filter-col">
              <div className={filterClass}>
                <div className={`product-listing__filter--sticky`}>
                  {propsFilters && Object.keys(propsFilters).length > 0 && (
                    <>
                      <span
                        id="filter-checkbox-instruction"
                        className="sr-only"
                        tabIndex="0"
                      >
                        {t("kf.plp.announce.filterCheckboxInstruction")}
                      </span>
                      <FiltersUI
                        propsScroll={propsScroll}
                        propsFilters={propsFilters}
                        propsSortUI={propsSortUI}
                        isMobileViewport={isMobileViewport}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className={tileClass}>
                {propsProductCard?.length === 0 ? (
                  <div
                    className="product-listing__no-results"
                    data-testid="no-results-message"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        t("kf.plp.label.noResultsMssg")
                      ),
                    }}
                  />
                ) : (
                  <div className={`product-listing__row row-cols-responsive`}>
                    {componentProps.addMultiPromo !== "true"
                      ? propsProductCard?.map((product, i) => {
                          if (
                            productCardSKUs.indexOf(product?.product?.sku) ===
                            -1
                          ) {
                            productCardSKUs.push(product?.product?.sku)
                            return (
                              <React.Fragment
                                key={`${i}-${product?.title?.text}`}
                              >
                                <div
                                  className="product-listing__tile"
                                  id={product?.product?.sku}
                                >
                                  <ProductCardUI
                                    propsProductCard={product}
                                    handleOnClickPartsDiagramLink={
                                      handleOnClickPartsDiagramLink
                                    }
                                    index={i}
                                  />
                                </div>
                                {componentProps.xfLink &&
                                  isDevicePresent &&
                                  (() => {
                                    if (shouldRepeatPromo) {
                                      return (
                                        (i + 1) % position === 0 &&
                                        (propsProductCard?.length > 11 ? (
                                          <div
                                            className={`${
                                              promoCols[
                                                componentProps.promoLayout
                                              ]
                                            } product-listing__tile`}
                                          >
                                            <div>
                                              <PromotionalTilePLP
                                                {...createPromoProps(i)}
                                              />
                                            </div>
                                          </div>
                                        ) : (
                                          propsProductCard?.length - 1 ===
                                            i && (
                                            <div
                                              className={`${
                                                promoCols[
                                                  componentProps.promoLayout
                                                ]
                                              } product-listing__tile`}
                                            >
                                              <div>
                                                <PromotionalTilePLP
                                                  {...createPromoProps(i)}
                                                />
                                              </div>
                                            </div>
                                          )
                                        ))
                                      )
                                    } else {
                                      return (
                                        i + 1 === position && (
                                          <div
                                            className={`${
                                              promoCols[
                                                componentProps.promoLayout
                                              ]
                                            } product-listing__tile`}
                                          >
                                            <div>
                                              <PromotionalTilePLP
                                                {...createPromoProps(i)}
                                              />
                                            </div>
                                          </div>
                                        )
                                      )
                                    }
                                  })()}
                              </React.Fragment>
                            )
                          }
                        })
                      : tiles?.map((tile, i) => {
                          return (
                            <React.Fragment key={`${i}-${tile?.title?.text}`}>
                              <div
                                className={`${
                                  tile?.productCardProps?.product?.sku
                                    ? ""
                                    : getPromoClass(width, tile?.promoLayout)
                                } product-listing__tile`}
                                id={tile?.productCardProps?.product?.sku}
                              >
                                {tile?.productCardProps?.product?.sku ? (
                                  <ProductCardUI
                                    propsProductCard={tile?.productCardProps}
                                    handleOnClickPartsDiagramLink={
                                      handleOnClickPartsDiagramLink
                                    }
                                    index={i}
                                  />
                                ) : tile?.ctaUrl &&
                                  !promoLinks[tile?.ctaUrl] ? (
                                  <div className="product-listing__tile--loading">
                                    Loading...
                                  </div>
                                ) : (
                                  <PromotionalTilePLP
                                    CTA={{
                                      text: tile?.ctaText,
                                      target:
                                        tile?.isNewTab === "true"
                                          ? "_blank"
                                          : "_self",
                                      href: tile?.ctaUrl
                                        ? promoLinks[tile?.ctaUrl]
                                        : actionLink,
                                      "aria-label": buildAriaLabel(
                                        tile?.ctaText,
                                        sanitizeText(tile?.heading)
                                      ),
                                      trackingAttributes: {
                                        "data-datalayer-id":
                                          "kds-element-promotile",
                                        "data-datalayer-data":
                                          JSON.stringify(
                                            getPromoEventInfo(
                                              (
                                                tile?.ctaText || ""
                                              ).toLowerCase(),
                                              tile?.ctaUrl
                                                ? promoLinks[tile?.ctaUrl]
                                                : actionLink
                                            )
                                          ) || "",
                                        "data-datalayer-component":
                                          JSON.stringify({
                                            componentID:
                                              typeof window !== "undefined" &&
                                              window.location.href,
                                            componentLocation:
                                              typeof window !== "undefined" &&
                                              window.location.pathname.includes(
                                                "search"
                                              )
                                                ? "search results:promo tiles"
                                                : "plp:promo tiles",
                                            componentMessage: `promo-${i}`,
                                            componentName: sanitizeText(
                                              tile?.heading
                                            )
                                              ?.replace(/<\/?[^>]+(>|$)/g, " ")
                                              .replace(/(\r\n|\n|\r)/gm, " ")
                                              .replace(/&nbsp;/g, " ")
                                              .toLowerCase(),
                                            componentType: "internal campaign",
                                          }) || "",
                                      },
                                    }}
                                    textPlacement={sanitizeText(
                                      tile?.textPlacement
                                    )}
                                    gradientDirection={tile?.gradientDirection}
                                    opacity={mapOverlayOpacity(tile?.opacity)}
                                    heading={{
                                      text: sanitizeText(tile?.heading),
                                    }}
                                    image={{
                                      src: scene7ImageUrl(
                                        tile?.image,
                                        scene7BaseUrl,
                                        dmScene7AccountName
                                      ),
                                      "data-mode": "smartcrop",
                                      alt: sanitizeText(tile?.imageAltText),
                                      role: "presentation",
                                    }}
                                    style={{
                                      aspectRatio: getPromoClass(
                                        width,
                                        tile?.promoLayout
                                      )?.includes("2")
                                        ? "2 / 1"
                                        : getPromoClass(
                                            width,
                                            tile?.promoLayout
                                          )?.includes("3")
                                        ? "3 / 1"
                                        : "1 / 1",
                                    }}
                                    mod={"black"}
                                    type={"1-column"}
                                  />
                                )}
                              </div>
                            </React.Fragment>
                          )
                        })}
                  </div>
                )}
                <Pagination {...propsPagination} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PartsViewerV2
        productId={partData?.productId}
        partId={partData?.partId}
        showModal={partData?.isShowModal}
        page={"PLP results"}
        onClose={onCloseModal}
        fileMap={svgFileMap}
      />
    </div>
  )
}
ProductListUI.propTypes = productListUIProps

export default ProductListUI
